import { useState } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { Logo } from "../components/ui/Logo";
import { LogoDark } from "../components/ui/LogoDark";

export function SignUpScreen({ onSignup, onLogin, defaultType }) {
  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState(defaultType || null);
  const [form, setForm] = useState({ name:"", email:"", password:"" });
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signupError, setSignupError] = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const pwScore = [form.password.length>=8, /[A-Z]/.test(form.password), /[0-9]/.test(form.password), /[^A-Za-z0-9]/.test(form.password)].filter(Boolean).length;
  const pwColors = ["",C.rose,C.amber,C.amber,C.sage];
  const pwLabels = ["","Weak","Fair","Good","Strong"];

  const create = async () => {
    if (!form.email||!form.password||!agreed) return;
    setLoading(true);
    setSignupError(null);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            user_type: userType,
            full_name: form.name,
          }
        }
      });
      if (error) throw error;
      // Set trial fields for new gallery accounts (7-day free trial, full Established access)
      if (userType === 'gallery' && data.user?.id) {
        await supabase.from('profiles').update({
          plan: 'established',
          trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          subscription_status: 'trialing',
        }).eq('id', data.user.id);
      }
      if (data.session) {
        // Email confirmation disabled — logged in immediately
        onSignup(userType);
      } else {
        // Email confirmation required — show check-your-email step
        setStep(2);
      }
    } catch (err) {
      setSignupError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="auth-visual">
        <div><Logo/></div>
        <div style={{ position:"relative",zIndex:1 }}>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.35rem",color:"#f0e2cc",fontWeight:300,lineHeight:1.5,marginBottom:14,fontStyle:"italic" }}>
            "As a gallery director, opencall. has transformed how we find emerging talent."
          </div>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".68rem",color:C.clay,letterSpacing:"1.5px",textTransform:"uppercase" }}>Sophie Reeves</div>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".6rem",color:"rgba(196,168,130,.4)",marginTop:3 }}>Director · The Foundry Gallery</div>
        </div>
        <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".6rem",color:"rgba(196,168,130,.28)",lineHeight:1.8 }}>
          7-day free trial · No credit card required · Cancel anytime
        </div>
      </div>
      <div className="auth-panel">
        <div style={{ maxWidth:400,width:"100%" }}>
          <div style={{ marginBottom:24 }}><LogoDark/></div>
          {step===0&&(
            <>
              <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.9rem",fontWeight:400,color:C.bark,marginBottom:6 }}>Join opencall.</h1>
              <p style={{ fontSize:".85rem",color:C.muted,marginBottom:24,lineHeight:1.65 }}>Australia's platform for artists, galleries and open calls.</p>
              {[
                { val:"artist",  icon:"🎨", label:"I'm an artist", desc:"Browse and apply to exhibitions, grants and residencies. Build a portfolio galleries can discover." },
                { val:"gallery", icon:"🏛️", label:"I represent a gallery or organisation", desc:"Post open calls, review applications and connect with Australian artists." },
              ].map(t=>(
                <button key={t.val} className={`type-card ${userType===t.val?"sel":""}`} onClick={()=>setUserType(t.val)}>
                  <div style={{ width:44,height:44,borderRadius:10,background:t.val==="artist"?"#d4c5a9":"#b8c9c2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.3rem",flexShrink:0 }}>{t.icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Fraunces',serif",fontSize:".98rem",color:C.bark,marginBottom:3 }}>{t.label}</div>
                    <div style={{ fontSize:".74rem",color:C.muted,lineHeight:1.5 }}>{t.desc}</div>
                  </div>
                  {userType===t.val&&<span style={{ color:C.clay,marginLeft:"auto",flexShrink:0 }}>✓</span>}
                </button>
              ))}
              <button className="btn-p" style={{ width:"100%",marginTop:8 }} disabled={!userType} onClick={()=>setStep(1)}>Continue →</button>
              <div style={{ marginTop:16,textAlign:"center",fontSize:".8rem",color:C.muted }}>
                Already have an account?{" "}
                <button onClick={onLogin} style={{ background:"none",border:"none",color:C.clay,fontWeight:500,cursor:"pointer",fontSize:".8rem",textDecoration:"underline" }}>Log in</button>
              </div>
            </>
          )}
          {step===1&&(
            <>
              <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark,marginBottom:6 }}>{userType==="artist"?"Your details":"Organisation details"}</h1>
              <p style={{ fontSize:".85rem",color:C.muted,marginBottom:24,lineHeight:1.65 }}>You'll complete your full profile after signing up.</p>
              {signupError && (
                <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:16,fontSize:".8rem",color:C.rose }}>⚠ {signupError}</div>
              )}
              <div className="form-group"><label className="form-label">{userType==="artist"?"Full name":"Gallery / organisation name"}</label><input className="form-input" placeholder={userType==="artist"?"Clara Moreau":"Gertrude Contemporary"} value={form.name} onChange={e=>set("name",e.target.value)}/></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" placeholder="your@email.com" value={form.email} onChange={e=>set("email",e.target.value)}/></div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{ position:"relative" }}>
                  <input className="form-input" type={showPw?"text":"password"} placeholder="At least 8 characters" value={form.password} onChange={e=>set("password",e.target.value)}/>
                  <button onClick={()=>setShowPw(!showPw)} style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.muted,minHeight:36,minWidth:36,display:"flex",alignItems:"center",justifyContent:"center" }}>{showPw?"🙈":"👁️"}</button>
                </div>
                {form.password&&(
                  <>
                    <div style={{ display:"flex",gap:4,marginTop:6 }}>
                      {[1,2,3,4].map(i=><div key={i} className="pw-strength" style={{ flex:1,background:i<=pwScore?pwColors[pwScore]:C.parchDD }}/>)}
                    </div>
                    <div style={{ fontSize:".68rem",color:pwColors[pwScore],fontFamily:"'DM Mono',monospace",marginTop:3 }}>{pwLabels[pwScore]}</div>
                  </>
                )}
              </div>
              <div style={{ display:"flex",alignItems:"flex-start",gap:10,marginBottom:20 }}>
                <button onClick={()=>setAgreed(!agreed)} style={{ width:22,height:22,borderRadius:4,border:`2px solid ${agreed?C.clay:C.sand}`,background:agreed?C.clay:"#fff",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:".72rem",flexShrink:0,marginTop:1,transition:"all .15s" }}>{agreed?"✓":""}</button>
                <div style={{ fontSize:".78rem",color:C.muted,lineHeight:1.6 }}>I agree to opencall.'s <span style={{ color:C.clay,textDecoration:"underline",cursor:"pointer" }}>Terms of Service</span> and <span style={{ color:C.clay,textDecoration:"underline",cursor:"pointer" }}>Privacy Policy</span></div>
              </div>
              <button className="btn-p" style={{ width:"100%" }} disabled={!form.email||!form.password||!agreed||loading} onClick={create}>
                {loading?"Creating account…":"Create Account →"}
              </button>
              <button className="btn-g" style={{ width:"100%",marginTop:10 }} onClick={()=>setStep(0)}>← Back</button>
            </>
          )}
          {step===2&&(
            <div style={{ textAlign:"center",paddingTop:20 }}>
              <div style={{ fontSize:"3rem",marginBottom:20 }}>✉️</div>
              <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark,marginBottom:10 }}>Check your email</h1>
              <p style={{ fontSize:".85rem",color:C.muted,lineHeight:1.75,marginBottom:24 }}>
                We've sent a confirmation link to <strong style={{ color:C.bark }}>{form.email}</strong>.<br/>
                Click the link to activate your account, then log in.
              </p>
              <button className="btn-p" style={{ width:"100%" }} onClick={onLogin}>Go to Login →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
